/**
 * Safe Markdown rendering (CommonMark + GFM: tables, strikethrough, task lists,
 * autolinks). User Markdown is untrusted (SECURITY.md §9):
 * - raw HTML is never rendered (`skipHtml`): no `<script>`, no event handlers;
 * - React elements only, no `dangerouslySetInnerHTML`;
 * - URLs pass react-markdown's `defaultUrlTransform` (drops `javascript:` etc.);
 * - images are never fetched automatically (a remote image would reveal the reader's IP address
 *   and "read" status to whoever wrote the URL): they become a link with the image's alt text;
 * - links open in a new tab with `rel="noopener noreferrer nofollow"`;
 * - a single newline is a visible line break (`remarkSoftBreaks`), like in a chat message.
 */
import type { ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { Link, useInRouterContext } from 'react-router';
import remarkGfm from 'remark-gfm';

import { internalPath } from '@/shared/lib/internal-links';
import { remarkSoftBreaks } from '@/shared/lib/remark-soft-breaks';

import styles from './Markdown.module.css';

/** A link to another Planosfera item opens in this tab through the router; anything else leaves
 * the application in a new tab. */
function MarkdownLink({
  href,
  title,
  children,
}: {
  href: string | undefined;
  title: string | undefined;
  children: ReactNode;
}) {
  const inRouter = useInRouterContext();
  const path = internalPath(href);
  if (path !== null && inRouter) {
    return (
      <Link to={path} {...(title && { title })}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} title={title} target="_blank" rel="noopener noreferrer nofollow">
      {children}
    </a>
  );
}

const components: Components = {
  a: ({ href, title, children }) => (
    <MarkdownLink href={href} title={title}>
      {children}
    </MarkdownLink>
  ),
  img: ({ src, alt }) =>
    src ? (
      <a href={src} target="_blank" rel="noopener noreferrer nofollow">
        {alt || src}
      </a>
    ) : null,
  // Task-list checkboxes are display-only; real checklists are checklist blocks.
  input: ({ type, checked }) => <input type={type} checked={checked} disabled readOnly />,
};

export function Markdown({ children }: { children: string }) {
  return (
    <div className={styles.markdown}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkSoftBreaks]} skipHtml components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
